import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import { Logo } from "../../components/Logo";
import Seo from "../../components/Seo";
import AuditForm from "../../components/marketing/AuditForm";
import { GO_CAMPAIGNS } from "../../data/site";

/* Minimal-distraction paid-ad landing page: /go/:slug */
export default function CampaignLanding() {
  const { slug } = useParams();
  const c = GO_CAMPAIGNS[slug];

  if (!c) {
    return (
      <div className="min-h-screen bg-alto-black text-white flex items-center justify-center p-6 text-center">
        <div><Logo dark size={34} /><p className="text-neutral-400 mt-6">Campaign not found.</p><Link to="/lead-system-audit" className="text-alto-red font-semibold mt-3 inline-block">Get your free audit →</Link></div>
      </div>
    );
  }
  const Icon = c.icon;

  return (
    <div className="min-h-screen bg-alto-black text-white font-sans">
      <Seo title={`${c.industry} — Free Lead System Audit | AllToMAIDIt`} description={c.sub} />
      {/* minimal header: logo only */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/"><Logo dark size={30} /></Link>
          <a href="#audit" className="btn-red text-sm font-bold px-4 py-2.5 rounded-md">GET A FREE AUDIT</a>
        </div>
      </header>

      <div className="marketing-grid-bg border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 eyebrow text-alto-red mb-5"><Icon size={16} /> For {c.industry}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.95]">{c.headline}</h1>
            <p className="text-neutral-300 mt-6 max-w-xl text-sm sm:text-lg leading-relaxed">{c.sub}</p>
            <div className="mt-7 space-y-2.5">
              {c.pains.map((p) => (
                <div key={p} className="flex items-start gap-3 text-neutral-200 text-sm"><AlertTriangle className="text-alto-red shrink-0 mt-0.5" size={16} />{p}</div>
              ))}
            </div>
            <a href="#audit" className="btn-red font-bold px-7 py-4 rounded-md inline-flex items-center gap-2 mt-8 text-sm">GET YOUR FREE LEAD SYSTEM AUDIT <ArrowRight size={18} /></a>
          </div>
          <div className="lg:col-span-2 hidden lg:block">
            <div className="rounded-2xl p-6 bg-alto-charcoal border border-white/10">
              <p className="eyebrow text-alto-red mb-4">From lead to customer</p>
              <div className="font-display font-bold space-y-1 text-sm">
                {["Traffic", "Capture", "CRM", "Follow-Up", "Appointment"].map((s) => (<React.Fragment key={s}><p>{s}</p><p className="text-neutral-600">↓</p></React.Fragment>))}
                <p className="text-alto-red">Customer</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* trust strip (process, no fabricated stats) */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 grid sm:grid-cols-3 gap-4">
        {["We audit where your leads leak", "We build the capture + follow-up system", "You stop losing paid leads"].map((t, i) => (
          <div key={i} className="flex items-center gap-3 text-sm text-neutral-300"><Check className="text-alto-red" size={18} />{t}</div>
        ))}
      </div>

      {/* AUDIT */}
      <div id="audit" className="border-t border-white/10 bg-gradient-to-br from-alto-red/15 to-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold leading-tight">Get your free Lead System Audit.</h2>
            <p className="text-neutral-300 mt-4 text-sm">Tell us how you handle leads today. We'll show you where customers are slipping away.</p>
          </div>
          <AuditForm campaign={c.campaign} />
        </div>
      </div>

      <footer className="border-t border-white/10 py-8 text-center text-xs text-neutral-500">© {new Date().getFullYear()} AllToMAIDIt Solutions · <Link to="/" className="hover:text-alto-red">alltomaidit.com</Link></footer>
    </div>
  );
}
